import { Alert } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  useGetMeshInstancesQuery,
  useGetMeshDocumentQuery,
} from '../requests/dataPipeline.gql.generated';
import { InstanceGraphState } from '../types/dataPipelineTypes';

export type InstanceMonitorContext = {
  meshRunId: DataPipeline.MeshRunId;
  state: InstanceGraphState;
  selectedNodeId: string | null;
  setSelectedNode: (nodeId: string | null) => void;
};

const instanceMonitorContext = createContext<InstanceMonitorContext | null>(
  null,
);

export const useInstanceGraph = (): InstanceMonitorContext => {
  const context = useContext(instanceMonitorContext);
  finleyAssert(
    context,
    'useInstanceGraph must be used within a InstanceGraphProvider.',
  );

  return context;
};

export const InstanceGraphProvider = ({
  children,
  meshRunId,
}: {
  children: React.ReactNode;
  meshRunId: DataPipeline.MeshRunId;
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { data: meshDocumentData } = useGetMeshDocumentQuery({
    variables: { meshRunId },
  });

  const { data: meshInstanceData } = useGetMeshInstancesQuery({
    variables: { meshRunId },
  });

  const meshDocument = useMemo(() => {
    if (!meshDocumentData?.meshDocument) return null;

    return DbGraphUtils.deserializeMeshDocument(meshDocumentData.meshDocument);
  }, [meshDocumentData?.meshDocument]);

  const graphData = useMemo(() => {
    if (!meshDocument) return null;
    if (!meshInstanceData?.meshInstances) return null;

    const artifactIdToInstanceMap = new Map<string, DataPipeline.Instance>();

    meshInstanceData.meshInstances.forEach((gqlInstance) => {
      // @dataPipeline: make this parsing typesafe
      const parsedInstance = JSON.parse(gqlInstance);

      const instance: DataPipeline.Instance = {
        id: parsedInstance.id,
        artifactId: parsedInstance.artifactId,
        status: parsedInstance.status,
        updated_at: new Date(parsedInstance.updated_at),
        created_at: new Date(parsedInstance.created_at),
        statusDetails: parsedInstance.statusDetails,
      };
      artifactIdToInstanceMap.set(instance.artifactId, instance);
    });

    const edges = meshDocument.edges ?? [];
    const nodes = meshDocument.artifacts.map((artifact) => ({
      artifact,
      instance: artifactIdToInstanceMap.get(artifact.id) || null,
    }));

    return {
      nodes,
      edges,
    };
  }, [meshDocument, meshInstanceData?.meshInstances]);

  const value = useMemo<InstanceMonitorContext | null>(() => {
    if (!graphData) return null;
    return {
      meshRunId,
      state: graphData,
      selectedNodeId,
      setSelectedNode: setSelectedNodeId,
    };
  }, [graphData, selectedNodeId, meshRunId]);

  if (!meshDocument) {
    return <Alert>No Mesh Document</Alert>;
  }
  if (!value) {
    return <Alert>No Value</Alert>;
  }

  return (
    <instanceMonitorContext.Provider value={value}>
      {value && children}
    </instanceMonitorContext.Provider>
  );
};

export type DisplayNode = {
  displayId: string;
  hasInputs: boolean;
  hasOutputs: boolean;
  artifact: DataPipeline.Artifact;
  instance: DataPipeline.Instance | null;
  isSelected: boolean;
};

export const useNode = (nodeId: string): DisplayNode | null => {
  const { state, selectedNodeId } = useInstanceGraph();
  const node = state.nodes.find(({ artifact }) => artifact.id === nodeId);
  if (!node) {
    return null;
  }

  const hasInputs = state.edges.some((edge) => edge.target === nodeId);
  const hasOutputs = state.edges.some((edge) => edge.source === nodeId);

  // generate a node displayId for the children to help manage state.
  const displayId = `displayNode_${node.artifact.id}`;
  return {
    displayId,
    hasInputs,
    hasOutputs,
    artifact: node.artifact,
    instance: node.instance,
    isSelected: selectedNodeId === node.artifact.id,
  };
};
